import React, { useState, useEffect, useRef } from 'react';
import { MessageSquare, X, Headphones, User } from 'lucide-react';
import { useChat } from '../../hooks/useChat';
import ChatWindow from './ChatWindow';
import ConversationList from './ConversationList';
import NewConversationModal from './NewConversationModal';
import { useSupabase } from '../../contexts/SupabaseContext';

interface ChatButtonProps {
  initialConversationId?: string;
  initialUserId?: string;
  initialTopic?: string;
}

const ChatButton: React.FC<ChatButtonProps> = ({
  initialConversationId,
  initialUserId,
  initialTopic
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [showNewConversationModal, setShowNewConversationModal] = useState(false);
  const [isMobileView, setIsMobileView] = useState(false);
  const [showConversationList, setShowConversationList] = useState(true);
  const [showAgentOptions, setShowAgentOptions] = useState(false);
  const chatRef = useRef<HTMLDivElement>(null);
  const { user } = useSupabase();
  
  const {
    conversations,
    currentConversation,
    setCurrentConversation,
    messages,
    loading,
    unreadCount,
    fetchConversations,
    fetchMessages,
    sendMessage,
    createConversation,
    findConversationBetweenUsers
  } = useChat();

  // Check if mobile view
  useEffect(() => {
    const checkMobile = () => {
      setIsMobileView(window.innerWidth < 768);
    };
    
    checkMobile();
    window.addEventListener('resize', checkMobile);
    
    return () => {
      window.removeEventListener('resize', checkMobile);
    };
  }, []);

  // Add click outside handler
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (chatRef.current && !chatRef.current.contains(event.target as Node)) {
        setIsOpen(false);
        setShowAgentOptions(false);
      }
    };

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }
    
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isOpen]);

  // Handle initial conversation or user
  useEffect(() => {
    const initializeChat = async () => {
      if (initialConversationId) {
        // Find conversation by ID
        const conversation = conversations.find(c => c.id === initialConversationId);
        if (conversation) {
          handleSelectConversation(conversation);
        }
      } else if (initialUserId) {
        // Find or create conversation with user
        const existingConversation = await findConversationBetweenUsers([initialUserId]);
        
        if (existingConversation) {
          const conversation = conversations.find(c => c.id === existingConversation.id);
          if (conversation) {
            handleSelectConversation(conversation);
          } else {
            await fetchConversations();
            const updatedConversation = conversations.find(c => c.id === existingConversation.id);
            if (updatedConversation) {
              handleSelectConversation(updatedConversation);
            }
          }
        } else if (isOpen) {
          // Create new conversation
          const newParticipants = [
            { user_id: initialUserId, role: 'seller' as const }
          ];
          
          const newConversation = await createConversation(
            newParticipants,
            initialTopic
          );
          
          if (newConversation) {
            await fetchConversations();
            const conversation = conversations.find(c => c.id === newConversation.id);
            if (conversation) {
              handleSelectConversation(conversation);
            }
          }
        }
      }
    };
    
    if ((initialConversationId || initialUserId) && isOpen && conversations.length > 0) {
      initializeChat();
    }
  }, [initialConversationId, initialUserId, isOpen, conversations]);

  const toggleChat = () => {
    setIsOpen(!isOpen);
    if (!isOpen) {
      fetchConversations();
    }
  };

  const handleSelectConversation = async (conversation: typeof currentConversation) => {
    setCurrentConversation(conversation);
    if (conversation) {
      await fetchMessages(conversation.id);
      if (isMobileView) {
        setShowConversationList(false);
      }
    }
  };

  const handleBackToList = () => {
    setShowConversationList(true);
  };

  const handleCloseChat = () => {
    setIsOpen(false);
    setShowAgentOptions(false);
  };

  const handleSendMessage = async (content: string, attachmentUrl?: string, attachmentType?: string) => {
    if (!currentConversation) return;
    await sendMessage(currentConversation.id, content, attachmentUrl, attachmentType);
  };

  const handleCreateConversation = async (
    participants: { user_id: string, role: 'buyer' | 'seller' | 'agent' }[],
    topic?: string
  ) => {
    const newConversation = await createConversation(participants, topic);
    if (newConversation) {
      await fetchConversations();
      const conversation = conversations.find(c => c.id === newConversation.id);
      if (conversation) {
        handleSelectConversation(conversation);
      }
    }
  };

  const handleContactAgent = async (topic: string) => {
    try {
      // Find an agent user (in a real app, you would have a way to find available agents)
      // For now, we'll use a hardcoded agent ID or the admin user
      const agentId = "55920128-db14-436e-b919-103dad542a73"; // This would be replaced with a real agent ID
      
      // Create a new conversation with the agent
      const newParticipants = [
        { user_id: agentId, role: 'agent' as const }
      ];
      
      const newConversation = await createConversation(
        newParticipants,
        topic
      );
      
      if (newConversation) {
        await fetchConversations();
        const conversation = conversations.find(c => c.id === newConversation.id);
        if (conversation) {
          handleSelectConversation(conversation);
          
          // Send an initial message
          await sendMessage(
            newConversation.id, 
            `Hello, I need assistance with ${topic}. Can you help me?`
          );
          
          // Refresh messages
          await fetchMessages(newConversation.id);
        }
      }
      
      setShowAgentOptions(false);
    } catch (error) {
      console.error('Error contacting agent:', error);
    }
  };

  return (
    <>
      {/* Chat Button */}
      <button
        onClick={toggleChat}
        className="fixed bottom-24 right-6 z-40 bg-orange-500 text-white p-4 rounded-full shadow-lg hover:bg-orange-600 transition-colors relative"
      >
        {isOpen ? (
          <X className="h-6 w-6" />
        ) : (
          <>
            <MessageSquare className="h-6 w-6" />
            {unreadCount > 0 && (
              <span className="absolute -top-2 -right-2 bg-red-500 text-white text-xs rounded-full h-5 w-5 flex items-center justify-center">
                {unreadCount > 9 ? '9+' : unreadCount}
              </span>
            )}
          </>
        )}
      </button>
      
      {/* Agent Contact Options */}
      {isOpen && showAgentOptions && (
        <div 
          className="fixed bottom-40 right-6 z-50 bg-white rounded-lg shadow-xl p-4 border border-gray-200 w-72"
        >
          <div className="flex justify-between items-center mb-3">
            <h3 className="font-medium text-gray-900">Contact Customer Service</h3>
            <button 
              onClick={() => setShowAgentOptions(false)}
              className="text-gray-400 hover:text-gray-500"
            >
              <X className="h-4 w-4" />
            </button>
          </div>
          
          <p className="text-sm text-gray-600 mb-4">
            Our agents are available 24/7 to assist you. What do you need help with?
          </p>
          
          <div className="space-y-2">
            <button
              onClick={() => handleContactAgent('Product Inquiry')}
              className="w-full text-left px-3 py-2 bg-orange-50 hover:bg-orange-100 text-orange-700 rounded-lg transition-colors flex items-center space-x-2"
            >
              <User className="h-4 w-4" />
              <span>Product Inquiry</span>
            </button>
            
            <button
              onClick={() => handleContactAgent('Order Support')}
              className="w-full text-left px-3 py-2 bg-blue-50 hover:bg-blue-100 text-blue-700 rounded-lg transition-colors flex items-center space-x-2"
            >
              <User className="h-4 w-4" />
              <span>Order Support</span>
            </button>
            
            <button
              onClick={() => handleContactAgent('Technical Support')}
              className="w-full text-left px-3 py-2 bg-purple-50 hover:bg-purple-100 text-purple-700 rounded-lg transition-colors flex items-center space-x-2"
            >
              <User className="h-4 w-4" />
              <span>Technical Support</span>
            </button>
            
            <button
              onClick={() => handleContactAgent('General Inquiry')}
              className="w-full text-left px-3 py-2 bg-green-50 hover:bg-green-100 text-green-700 rounded-lg transition-colors flex items-center space-x-2"
            >
              <User className="h-4 w-4" />
              <span>General Inquiry</span>
            </button>
          </div>
        </div>
      )}
      
      {/* Chat Window */}
      {isOpen && (
        <div 
          ref={chatRef}
          className="fixed bottom-24 right-6 z-40 w-96 h-[500px] bg-white rounded-lg shadow-xl overflow-hidden border border-gray-200 flex flex-col md:w-[700px]"
        >
          {/* Close button at the top right */}
          <button
            onClick={handleCloseChat}
            className="absolute top-2 right-2 z-50 p-1 bg-gray-100 hover:bg-gray-200 rounded-full text-gray-500 hover:text-gray-700 transition-colors"
            title="Close chat"
          >
            <X className="h-5 w-5" />
          </button>
          
          <div className="flex-1 flex overflow-hidden">
            {/* Conversation List - Show on desktop or when in list view on mobile */}
            {(!isMobileView || showConversationList) && (
              <div className={`${isMobileView ? 'w-full' : 'w-1/3 border-r border-gray-200'}`}>
                <div className="flex flex-col h-full">
                  <ConversationList
                    conversations={conversations}
                    selectedConversation={currentConversation}
                    onSelectConversation={handleSelectConversation}
                    onNewConversation={() => setShowNewConversationModal(true)}
                    loading={loading}
                  />
                  
                  {/* Contact Agent Button */}
                  <div className="p-3 bg-gray-50 border-t border-gray-200">
                    <button
                      onClick={() => setShowAgentOptions(true)}
                      className="w-full flex items-center justify-center space-x-2 bg-blue-600 text-white py-2 rounded-lg hover:bg-blue-700 transition-colors text-sm"
                    >
                      <Headphones className="h-4 w-4" />
                      <span>Contact with Agent</span>
                    </button>
                  </div>
                </div>
              </div>
            )}
            
            {/* Chat Window - Show on desktop or when in chat view on mobile */}
            {(!isMobileView || !showConversationList) && (
              <div className={`${isMobileView ? 'w-full' : 'w-2/3'}`}>
                <ChatWindow
                  conversation={currentConversation}
                  messages={messages}
                  onSendMessage={handleSendMessage}
                  onBack={isMobileView ? handleBackToList : undefined}
                  onClose={handleCloseChat}
                  loading={loading}
                />
              </div>
            )}
          </div>
        </div>
      )}
      
      {/* New Conversation Modal */}
      <NewConversationModal
        isOpen={showNewConversationModal}
        onClose={() => setShowNewConversationModal(false)}
        onCreateConversation={handleCreateConversation}
      />
    </>
  );
};

export default ChatButton;